import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { safePush as safeRouterPush } from '@/modules/navigation';

import { useLaunchSetupStore } from '@/stores/LaunchSetup';
import { useLanguageStore } from '@/stores/language';
import { useLocationStore } from '@/stores/location';
import { usePreferenceStore } from '@/stores/preference';

export function useNavigation() {
  const route = useRoute();
  const router = useRouter();

  const launchSetupStore = useLaunchSetupStore();
  const languageStore = useLanguageStore();
  const locationStore = useLocationStore();
  const preferenceStore = usePreferenceStore();

  const safePush = (target: string | object): Promise<void> => safeRouterPush(router, target);

  const getNextFromPreferences = async (): Promise<string> => {
    let next: string | undefined;

    if (locationStore.locationsIncomplete) {
      next = '/location';
    }

    if (!next) {
      const preference = preferenceStore.nextIncomplete;

      next = !preference?.name || preference.name === 'summary'
        ? '/assessment_entry'
        : `/preference/${preference.name}`;
    }

    return next;
  };

  const ready = computed<boolean>(() => {
    if (route.meta?.ready) return true;

    switch (route.name) {
      case 'Language':
        if (route.query?.setup === 'true') {
          return launchSetupStore.configurationReady && languageStore.all.some(
            (language) => language.value === languageStore.selected,
          );
        }
        return languageStore.source === 'user';
      case 'Location':
        return locationStore.selectionMade;
      case 'Preference': {
        const currentPreference = preferenceStore.get(route.params.panels_name);
        return currentPreference?.responses?.some((response: any) => response.selected) ?? false;
      }
      default:
        return false;
    }
  });

  const showPrevious = computed<boolean>(() => {
    let showPrev = true;

    switch (route.name) {
      case 'Location':
        showPrev = false;
        break;
    }

    return showPrev;
  });

  const previous = (): void => {
    router.go(-1);
  };

  const to = async (payload: string | object): Promise<void> => {
    await safePush(payload);
  };

  const nextLink = async (): Promise<string> => {
    let nextRoute: string;

    switch (route.name) {
      case 'Start':
        nextRoute = '/assessment_entry';
        break;
      case 'Language':
        nextRoute = route.query?.from === 'user_interface'
          ? '/preference/user_interface'
          : await getNextFromPreferences();
        break;
      case 'Location':
      case 'Preference':
        nextRoute = await getNextFromPreferences();
        break;
      case 'Summary':
        nextRoute = route.query?.setup === 'true'
          ? '/start'
          : '/assessment_entry';
        break;
      default:
        nextRoute = '/start';
        break;
    }

    return nextRoute;
  };

  const next = async (): Promise<void> => {
    if (route.name === 'Language' && route.query?.setup === 'true') {
      if (!ready.value) return;
      languageStore.completeInitialSetup();
      await safePush(launchSetupStore.firstGroupRoute);
      return;
    }

    const target = await nextLink();
    await safePush(target);
  };

  return {
    ready,
    showPrevious,
    previous,
    to,
    nextLink,
    next,
  };
}
